import React, { useState, useMemo } from 'react';
import { Order, MenuItem, CartItem } from '../types';
import { Download, Edit2, Trash2, X, ChevronDown, ChevronUp, FileText, Plus, Minus, Filter, CalendarDays, CalendarRange, Calendar, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLongPress, SwipeNumberInput } from './MobileControls';

interface HistoryViewProps {
  orders: Order[];
  menuItems: MenuItem[];
  onDeleteOrder: (id: string) => void;
  onRestoreOrder: (id: string) => void;
  onUpdateOrder: (order: Order) => void;
}

type FilterType = 'all' | 'daily' | 'monthly' | 'range';

interface OrderRowProps { 
  order: Order; 
  menuItems: MenuItem[];
  onDelete: (id: string) => void; 
  onRestore: (id: string) => void;
  onEdit: (order: Order) => void;
}

// Extracted OrderRow to prevent remounting and event loss
const OrderRow: React.FC<OrderRowProps> = ({ 
  order, 
  menuItems,
  onDelete, 
  onRestore,
  onEdit 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handlePhysicalDelete = () => {
      if(confirm('論理削除されています。完全に消去しますか？（復元できなくなります）')) {
         // Since the app currently only supports logical delete via onDeleteOrder, 
         // we would need a physical delete function if we wanted to truly remove it.
         // For now, we reuse onDelete which sets isDeleted=true (idempotent)
         // or we could assume this is the end of the line.
         // Actually, if it's already deleted, maybe we don't need a physical delete button exposed easily.
         // Let's keep it simple: "Restore" or "Delete" (Logical).
      }
  };

  const longPressHandlers = useLongPress(() => {
     if (!order.isDeleted) {
        if (confirm(`この売上記録を削除しますか？\n${order.label} - ¥${order.total}`)) {
            onDelete(order.id);
        }
     }
  });

  const stopProp = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className={`bg-white border ${order.isDeleted ? 'border-red-100 bg-red-50/30' : 'border-stone-100'} rounded-2xl overflow-hidden shadow-sm select-none transition-colors`} {...longPressHandlers}>
            <div 
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-4 flex justify-between items-center cursor-pointer hover:bg-stone-50 transition-colors active:bg-stone-100"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${order.isDeleted ? 'bg-red-100 text-red-400' : 'bg-stone-100 text-stone-500'}`}>
                  {order.isDeleted ? <Trash2 size={20} /> : <FileText size={20} />}
                </div>
                <div className={order.isDeleted ? 'opacity-50' : ''}>
                  <div className={`font-bold text-stone-800 ${order.isDeleted ? 'line-through decoration-red-400' : ''}`}>¥{order.total.toLocaleString()}</div>
                  <div className="flex flex-col sm:flex-row sm:gap-2 text-xs text-stone-400">
                    <span>{new Date(order.timestamp).toLocaleString()}</span>
                    <span className="hidden sm:inline">|</span>
                    <span className="text-stone-500 font-bold">{order.label}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-stone-400">
                <span className="text-xs">{order.items.reduce((s,i)=>s+i.quantity,0)}点</span>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>

            <AnimatePresence>
              {isExpanded && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-stone-50 border-t border-stone-100 overflow-hidden"
                >
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-stone-200">
                       <div className="text-xs text-stone-500 font-bold">{order.label}</div>
                       {!order.isDeleted && (
                         <button 
                           type="button"
                           onMouseDown={stopProp}
                           onTouchStart={stopProp}
                           onClick={(e) => { stopProp(e); onEdit(order); }}
                           className="text-stone-500 hover:text-stone-800 p-2 rounded relative z-10 cursor-pointer flex items-center gap-1 text-xs font-bold"
                         >
                           <Edit2 size={12} /> 編集
                         </button>
                       )}
                    </div>
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm text-stone-600">
                        <span>{item.name} <span className="text-xs text-stone-400">×{item.quantity}</span></span>
                        <span className="font-mono">¥{(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="pt-4 flex justify-end">
                      {order.isDeleted ? (
                         <button 
                           type="button"
                           onMouseDown={stopProp}
                           onTouchStart={stopProp}
                           onClick={(e) => {
                             stopProp(e);
                             if(confirm('この注文を復元しますか？')) onRestore(order.id);
                           }}
                           className="bg-stone-200 text-stone-600 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-stone-300 relative z-10 cursor-pointer"
                         >
                           <RotateCcw size={14} /> 復元する
                         </button>
                      ) : (
                        <button 
                          type="button"
                          onMouseDown={stopProp}
                          onTouchStart={stopProp}
                          onClick={(e) => {
                            stopProp(e);
                            if(confirm('本当にこの注文を削除しますか？')) onDelete(order.id);
                          }}
                          className="bg-red-50 text-red-400 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-red-100 relative z-10 cursor-pointer"
                        >
                          <Trash2 size={14} /> 削除
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
  );
};

export const HistoryView: React.FC<HistoryViewProps> = ({ orders, menuItems, onDeleteOrder, onRestoreOrder, onUpdateOrder }) => {
  // Filter State
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [filterStart, setFilterStart] = useState(new Date().toISOString().split('T')[0]);
  const [filterEnd, setFilterEnd] = useState(new Date().toISOString().split('T')[0]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [editDate, setEditDate] = useState('');
  
  // Time state split for swipe inputs
  const [editHour, setEditHour] = useState(12);
  const [editMinute, setEditMinute] = useState(0);

  // Filter Logic
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Logical Delete Filter
    if (!showDeleted) {
      result = result.filter(o => !o.isDeleted);
    }

    switch (filterType) {
      case 'daily':
        result = result.filter(o => {
          const d = new Date(o.timestamp);
          const dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
          return dateStr === filterDate;
        });
        break;
      case 'monthly':
        result = result.filter(o => {
          const d = new Date(o.timestamp);
          const monthStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
          return monthStr === filterMonth;
        });
        break;
      case 'range':
        const start = new Date(filterStart).getTime();
        const end = new Date(filterEnd).getTime() + (24 * 60 * 60 * 1000) - 1; // End of day
        result = result.filter(o => o.timestamp >= start && o.timestamp <= end);
        break;
      case 'all':
      default:
        break;
    }

    return result.sort((a, b) => b.timestamp - a.timestamp);
  }, [orders, filterType, filterDate, filterMonth, filterStart, filterEnd, showDeleted]);

  // Calculate totals excluding deleted items unless explicitly handled (usually stats exclude deleted)
  const activeFilteredOrders = filteredOrders.filter(o => !o.isDeleted);
  const totalSales = activeFilteredOrders.reduce((sum, order) => sum + order.total, 0);

  const handleDownloadCSV = () => {
    // CSV Header
    const headers = ['Date', 'Time', 'OrderID', 'Table/Customer', 'ItemName', 'Price', 'Quantity', 'Subtotal', 'Category', 'Status'];
    
    // CSV Rows
    const rows = filteredOrders.flatMap(order => {
      const date = new Date(order.timestamp);
      const dateStr = date.toLocaleDateString();
      const timeStr = date.toLocaleTimeString();
      const status = order.isDeleted ? 'Deleted' : 'Active';
      
      return order.items.map(item => [
        dateStr,
        timeStr,
        order.id,
        order.label, // Includes Table Name - Customer Name
        item.name,
        item.price,
        item.quantity,
        item.price * item.quantity,
        item.category,
        status
      ]);
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Dynamic Filename
      let filename = 'sales_all.csv';
      if (filterType === 'daily') filename = `sales_daily_${filterDate}.csv`;
      if (filterType === 'monthly') filename = `sales_monthly_${filterMonth}.csv`;
      if (filterType === 'range') filename = `sales_range_${filterStart}_to_${filterEnd}.csv`;

      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openEditModal = (order: Order) => {
    const d = new Date(order.timestamp);
    // Format date for input type="date"
    const dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    
    // Parse time for swipe inputs
    const h = d.getHours();
    const m = d.getMinutes();

    setEditingOrder({ ...order }); 
    setEditDate(dateStr);
    setEditHour(h);
    setEditMinute(m);
    setIsEditModalOpen(true);
  };

  const handleEditItemQuantity = (itemId: string, delta: number) => {
    if (!editingOrder) return;
    
    const newItems = editingOrder.items.map(item => {
      if (item.id === itemId) {
        return { ...item, quantity: Math.max(0, item.quantity + delta) };
      }
      return item;
    }).filter(item => item.quantity > 0);

    const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setEditingOrder({ ...editingOrder, items: newItems, total: newTotal });
  };

  const handleAddItemToOrder = (menuItem: MenuItem) => {
    if (!editingOrder) return;
    
    const existing = editingOrder.items.find(i => i.id === menuItem.id);
    let newItems: CartItem[];
    if (existing) {
      newItems = editingOrder.items.map(i => i.id === menuItem.id ? { ...i, quantity: i.quantity + 1 } : i);
    } else {
      newItems = [...editingOrder.items, { ...menuItem, quantity: 1 }];
    }
    const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setEditingOrder({ ...editingOrder, items: newItems, total: newTotal });
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrder) return;

    // Reconstruct timestamp
    const formattedTime = `${editHour.toString().padStart(2, '0')}:${editMinute.toString().padStart(2, '0')}`;
    const newTimestamp = new Date(`${editDate}T${formattedTime}`).getTime();

    onUpdateOrder({
      ...editingOrder,
      timestamp: newTimestamp
    });
    setIsEditModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Filters Section */}
      <div className="bg-white rounded-3xl p-4 shadow-sm border border-stone-100 mb-4 shrink-0">
         <button 
           onClick={() => setIsFilterOpen(!isFilterOpen)}
           className="flex items-center gap-2 text-sm font-bold text-stone-600 w-full justify-between"
         >
           <span className="flex items-center gap-2">
             <Filter size={18} />
             絞り込み・集計
             <span className="text-xs font-normal text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
               {filterType === 'all' ? '全期間' : 
                filterType === 'daily' ? `日次: ${filterDate}` :
                filterType === 'monthly' ? `月次: ${filterMonth}` : '期間指定'}
             </span>
           </span>
           {isFilterOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
         </button>

         <AnimatePresence>
            {isFilterOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 space-y-4 border-t border-stone-100 mt-3">
                   {/* Toggle Show Deleted */}
                   <div className="flex justify-end">
                      <button 
                        onClick={() => setShowDeleted(!showDeleted)}
                        className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors ${showDeleted ? 'bg-red-50 text-red-600' : 'bg-stone-50 text-stone-500'}`}
                      >
                         {showDeleted ? <Eye size={14} /> : <EyeOff size={14} />}
                         {showDeleted ? 'ゴミ箱を隠す' : 'ゴミ箱を表示'}
                      </button>
                   </div>

                   {/* Filter Type Tabs */}
                   <div className="flex bg-stone-100 p-1 rounded-xl">
                      <button 
                        onClick={() => setFilterType('all')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors ${filterType === 'all' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      >
                        全期間
                      </button>
                      <button 
                        onClick={() => setFilterType('daily')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1 ${filterType === 'daily' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      >
                        <CalendarDays size={12} /> 日次
                      </button>
                      <button 
                        onClick={() => setFilterType('monthly')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1 ${filterType === 'monthly' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      >
                        <Calendar size={12} /> 月次
                      </button>
                      <button 
                        onClick={() => setFilterType('range')}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-1 ${filterType === 'range' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
                      >
                        <CalendarRange size={12} /> 期間
                      </button>
                   </div>
                   
                   {/* Inputs */}
                   <div className="bg-stone-50 p-3 rounded-xl border border-stone-100">
                      {filterType === 'all' && <div className="text-center text-xs text-stone-400 py-2">すべての売上履歴を表示します</div>}
                      
                      {filterType === 'daily' && (
                        <div className="flex items-center gap-2">
                           <label className="text-xs font-bold text-stone-500">日付:</label>
                           <input 
                             type="date" 
                             className="flex-1 bg-white border border-stone-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-200"
                             value={filterDate}
                             onChange={(e) => setFilterDate(e.target.value)}
                           />
                        </div>
                      )}

                      {filterType === 'monthly' && (
                        <div className="flex items-center gap-2">
                           <label className="text-xs font-bold text-stone-500">月:</label>
                           <input 
                             type="month" 
                             className="flex-1 bg-white border border-stone-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-200"
                             value={filterMonth}
                             onChange={(e) => setFilterMonth(e.target.value)}
                           />
                        </div>
                      )}

                      {filterType === 'range' && (
                        <div className="space-y-2">
                           <div className="flex items-center gap-2">
                             <label className="text-xs font-bold text-stone-500 w-8">開始:</label>
                             <input 
                               type="date" 
                               className="flex-1 bg-white border border-stone-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-200"
                               value={filterStart}
                               onChange={(e) => setFilterStart(e.target.value)}
                             />
                           </div>
                           <div className="flex items-center gap-2">
                             <label className="text-xs font-bold text-stone-500 w-8">終了:</label>
                             <input 
                               type="date" 
                               className="flex-1 bg-white border border-stone-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-200"
                               value={filterEnd}
                               onChange={(e) => setFilterEnd(e.target.value)}
                             />
                           </div>
                        </div>
                      )}
                   </div>
                </div>
              </motion.div>
            )}
         </AnimatePresence>
      </div>

      {/* Stats & List */}
      <div className="bg-stone-800 text-white p-6 rounded-3xl shadow-lg mb-6 flex justify-between items-center shrink-0">
        <div>
          <h2 className="text-sm text-stone-400 mb-1">売上総額 ({filterType === 'all' ? '累計' : '選択範囲'})</h2>
          <div className="text-3xl font-bold font-mono">¥{totalSales.toLocaleString()}</div>
          <div className="text-xs text-stone-500 mt-1">{activeFilteredOrders.length} 件の注文 (削除済み除く)</div>
        </div>
        <button 
          onClick={handleDownloadCSV}
          className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-xl flex flex-col items-center gap-1 transition-colors"
        >
          <Download size={20} />
          <span className="text-[10px] font-bold">CSV</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pb-6">
        <p className="text-xs text-stone-400 text-center mb-2">長押しで削除</p>
        {filteredOrders.length === 0 ? (
          <div className="text-center py-20 text-stone-400">
            <p>対象期間の売上履歴はありません</p>
          </div>
        ) : (
          filteredOrders.map(order => (
             <OrderRow 
               key={order.id} 
               order={order} 
               menuItems={menuItems}
               onDelete={onDeleteOrder} 
               onRestore={onRestoreOrder}
               onEdit={openEditModal} 
             />
          ))
        )}
      </div>

      {/* Edit History Modal */}
      <AnimatePresence>
        {isEditModalOpen && editingOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
              <div className="p-4 border-b border-stone-100 flex justify-between items-center bg-stone-50 shrink-0">
                <h3 className="font-bold text-lg">注文履歴の修正</h3>
                <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-stone-200 rounded-full transition-colors"><X size={20} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <form id="edit-history-form" onSubmit={handleSaveEdit} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-stone-500 block mb-1">宛名（テーブル - 顧客名）</label>
                    <input 
                      type="text" 
                      required
                      className="w-full bg-stone-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-stone-200"
                      value={editingOrder.label}
                      onChange={e => setEditingOrder({...editingOrder, label: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                       <label className="text-xs font-bold text-stone-500 block mb-1">日付</label>
                       <input 
                         type="date" 
                         required
                         className="w-full bg-stone-50 border-none rounded-xl py-3 px-4"
                         value={editDate}
                         onChange={e => setEditDate(e.target.value)}
                       />
                     </div>
                     <div>
                       <label className="text-xs font-bold text-stone-500 block mb-1">時間</label>
                        <div className="flex gap-2">
                           <div className="flex-1">
                              <SwipeNumberInput 
                                 value={editHour} 
                                 onChange={setEditHour} 
                                 min={0} max={23} 
                                 label="時"
                                 unit="時"
                              />
                           </div>
                           <div className="flex-1">
                              <SwipeNumberInput 
                                 value={editMinute} 
                                 onChange={setEditMinute} 
                                 min={0} max={59} 
                                 format={v => v.toString().padStart(2, '0')}
                                 label="分"
                                 unit="分"
                              />
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="border-t border-stone-100 pt-4">
                    <label className="text-xs font-bold text-stone-500 block mb-2">注文内容</label>
                    <div className="space-y-2 mb-4">
                      {editingOrder.items.map(item => (
                        <div key={item.id} className="flex justify-between items-center bg-stone-50 p-2 rounded-lg">
                           <span className="text-sm font-bold flex-1">{item.name}</span>
                           <div className="flex items-center gap-2">
                             <button type="button" onClick={() => handleEditItemQuantity(item.id, -1)} className="p-1 text-stone-400 hover:bg-white rounded"><Minus size={14}/></button>
                             <span className="w-6 text-center text-sm">{item.quantity}</span>
                             <button type="button" onClick={() => handleEditItemQuantity(item.id, 1)} className="p-1 text-stone-400 hover:bg-white rounded"><Plus size={14}/></button>
                           </div>
                        </div>
                      ))}
                    </div>

                    <label className="text-xs font-bold text-stone-500 block mb-2">商品追加</label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto bg-stone-50 p-2 rounded-xl">
                       {menuItems.map(item => (
                         <button 
                           key={item.id}
                           type="button"
                           onClick={() => handleAddItemToOrder(item)}
                           className="text-left text-xs p-2 bg-white rounded-lg border border-stone-100 hover:border-orange-200 transition-colors flex justify-between"
                         >
                           <span className="truncate">{item.name}</span>
                           <span>+</span>
                         </button>
                       ))}
                    </div>
                  </div>
                </form>
              </div>

              <div className="p-4 border-t border-stone-100 bg-stone-50 shrink-0">
                 <div className="flex justify-between items-center mb-4 font-bold">
                    <span>合計金額（再計算）</span>
                    <span className="text-xl">¥{editingOrder.total.toLocaleString()}</span>
                 </div>
                 <button 
                   type="submit"
                   form="edit-history-form"
                   className="w-full bg-stone-800 text-white font-bold py-3 rounded-xl hover:bg-stone-700 transition-colors"
                 >
                   変更内容を保存
                 </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};